package link.erpango.quip.data.remote.repository.maintenance

import kotlinx.serialization.builtins.ListSerializer
import kotlinx.serialization.builtins.serializer
import link.erpango.quip.data.remote.ApiClient
import link.erpango.quip.data.remote.DResponse
import link.erpango.quip.data.remote.repository.IApiRepository
import link.erpango.quip.domain.entities.DMaintenance
import link.erpango.quip.domain.entities.DVehicleMaintenance

class MaintenanceRepository (
    override val api: ApiClient,
): IMaintenanceRepository, IApiRepository<ApiClient> {
    override val resourcePath: String
        get() = "api/maintenances"

    /**
     * Obtiene todos los mantenimientos de un vehículo específico.
     * @param vehicleVin El VIN del vehículo del cual se quieren obtener los mantenimientos.
     * @return DResponse con la lista de mantenimientos del vehículo.
     */
    override suspend fun getMaintenancesByVehicle(vehicleVin: String): DResponse<List<DMaintenance>> {
        return this.api.consume("$resourcePath/?vehicle_vin=$vehicleVin")
            .get()
            .response(ListSerializer(DMaintenance.serializer()))
    }

    /**
     * Crea un nuevo mantenimiento para un vehículo.
     * @param maintenance El objeto DMaintenance con los datos del mantenimiento.
     * @param vehicleVin El VIN del vehículo al que se asociará el mantenimiento.
     * @return DResponse con el mantenimiento creado.
     */
    override suspend fun createMaintenance(maintenance: DMaintenance, vehicleVin: String): DResponse<DMaintenance> {
        // Creamos un objeto DMaintenance con el campo vehicleVin establecido
        val maintenanceWithVehicle = DMaintenance(
            id = maintenance.id,
            vehicleVin = vehicleVin,
            date = maintenance.date,
            cost = maintenance.cost,
            kilometers = maintenance.kilometers,
            description = maintenance.description,
            mechanic = maintenance.mechanic,
            workshop = maintenance.workshop,
            spareParts = maintenance.spareParts
        )

        return this.api.consume("$resourcePath/")
            .post(maintenanceWithVehicle, DMaintenance.serializer())
            .response(DMaintenance.serializer())
    }

    /**
     * Obtiene el detalle de un mantenimiento específico.
     * @param id El ID del mantenimiento.
     * @return DResponse con el detalle del mantenimiento.
     */
    override suspend fun getMaintenanceDetail(id: Long): DResponse<DMaintenance> {
        return this.api.consume("$resourcePath/$id/")
            .get()
            .response(DMaintenance.serializer())
    }

    /**
     * Actualiza un mantenimiento existente.
     * @param id El ID del mantenimiento a actualizar.
     * @param maintenance El objeto DMaintenance con los datos actualizados.
     * @return DResponse con el mantenimiento actualizado.
     */
    override suspend fun updateMaintenance(id: Long, maintenance: DMaintenance): DResponse<DMaintenance> {
        return this.api.consume("$resourcePath/$id/")
            .put(maintenance, DMaintenance.serializer())
            .response(DMaintenance.serializer())
    }

    /**
     * Elimina (soft delete) un mantenimiento existente.
     * Solo se pueden eliminar mantenimientos que no estén firmados.
     * @param id El ID del mantenimiento a eliminar.
     * @return DResponse vacío si la eliminación fue exitosa, o un error si falló.
     */
    override suspend fun deleteMaintenance(id: Long): DResponse<Unit> {
        return this.api.consume("$resourcePath")
            .delete(id.toInt())
            .response(Unit.serializer())

    }
}